import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthCard, Label, Legend, PasswordInput, authFieldClass } from '../components/AuthCard';
import Combobox, { type ComboboxOption } from '../components/Combobox';
import { api, type ApiError } from '../lib/api';
import { SsoLink, SsoNote, providerFor } from '../lib/sso';

/*
  Registration asks for an account; it does not create access. The server files
  an inactive, unapproved request either way, so nothing here is a shortcut past
  the training office.

  Two entry points share this form:
    /register          — filled in from scratch
    /register/complete — returning from an SSO provider, name and email verified
  The basic scopes carry no gender, phone or locality, so even a verified Google
  or Microsoft user has to answer the same questions.
*/

interface Props {
  /** Completing an SSO registration rather than starting a fresh one. */
  fromSso?: boolean;
}

interface Prefill {
  fullName: string;
  email: string;
  /** Which provider verified the two fields above — 'Google', 'Microsoft'. */
  provider: string | null;
}

/** Mirrors the server's rule; checked here first so the message lands on the field. */
const MIN_PASSWORD = 10;

const GENDERS = [
  { value: 'male', label: 'Brother' },
  { value: 'female', label: 'Sister' },
] as const;

export default function Register({ fromSso = false }: Props) {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [gender, setGender] = useState<'male' | 'female' | ''>('');
  const [phone, setPhone] = useState('');
  const [locality, setLocality] = useState<ComboboxOption | null>(null);
  const [localities, setLocalities] = useState<ComboboxOption[]>([]);
  // Only for a fresh registration — an SSO account signs in with its provider.
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');

  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [loadingPrefill, setLoadingPrefill] = useState(fromSso);
  // Named so the locked fields can say who vouched for them.
  const [provider, setProvider] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);
  // Which SSO buttons exist, so an address is only redirected to a provider
  // that is actually configured.
  const [ssoReady, setSsoReady] = useState<string[]>([]);
  // Null until the server answers; only a definite "closed" swaps out the form.
  const [registrationOpen, setRegistrationOpen] = useState<boolean | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    api<{ providers: string[]; registrationOpen: boolean }>('/auth/providers')
      .then((r) => {
        if (cancelled) return;
        setSsoReady(r.providers);
        setRegistrationOpen(r.registrationOpen);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // A curated list, so the same church cannot arrive as "KL", "Kuala Lumpur"
  // and "K.L." across three accounts.
  useEffect(() => {
    let cancelled = false;
    api<{ localities: ComboboxOption[] }>('/localities')
      .then((r) => !cancelled && setLocalities(r.localities))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // The provider already verified these, so they are shown but not editable.
  useEffect(() => {
    if (!fromSso) return;
    let cancelled = false;
    api<{ registration: Prefill }>('/auth/register/pending')
      .then((r) => {
        if (cancelled) return;
        setFullName(r.registration.fullName);
        setEmail(r.registration.email);
        setProvider(r.registration.provider);
      })
      .catch(() => !cancelled && setExpired(true))
      .finally(() => !cancelled && setLoadingPrefill(false));
    return () => {
      cancelled = true;
    };
  }, [fromSso]);

  /*
    Gmail and Outlook addresses do not get a password here — the provider
    already holds one, and a second would leave the person guessing which this
    site wanted. The server refuses these too; this only saves them filling in
    the rest of the form first.
  */
  const owner = fromSso ? undefined : providerFor(email, ssoReady);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    /*
      Checked in the order the fields appear, so an empty form complains about
      the first thing missing rather than whichever check happens to run first.
      The form carries noValidate — the browser's own bubbles cannot be styled
      and read badly on mobile — so every rule lives here instead.
    */
    const problems: [id: string, message: string | null][] = [
      ['fullName', fullName.trim().length < 2 ? 'Enter your full name.' : null],
      ['email', !/^\S+@\S+\.\S+$/.test(email.trim()) ? 'Enter a valid email address.' : null],
      ['email', owner ? `${owner.label} addresses sign in with ${owner.label}. Use the button in the notice below.` : null],
      ['gender-male', !gender ? 'Choose a gender.' : null],
      ['phone', phone.trim().length < 5 ? 'Enter your mobile number.' : null],
      ['locality', !locality ? 'Choose your locality from the list.' : null],
      ['password', !fromSso && password.length < MIN_PASSWORD ? `Use a password of at least ${MIN_PASSWORD} characters.` : null],
      ['confirm', !fromSso && confirm !== password ? 'The passwords do not match.' : null],
    ];
    const firstProblem = problems.find(([, message]) => message);
    if (firstProblem) {
      const [id, message] = firstProblem;
      setError(message);
      const field = document.getElementById(id);
      field?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      field?.focus({ preventScroll: true });
      return;
    }
    // The checks above already cover these; this narrows the types for the
    // compiler, which cannot see through the array.
    const localityId = locality?.id;
    if (!gender || !localityId) return;

    setError(null);
    setSubmitting(true);
    try {
      if (fromSso) {
        await api('/auth/register/complete', {
          method: 'POST',
          body: JSON.stringify({ gender, phone, localityId }),
        });
      } else {
        await api('/auth/register', {
          method: 'POST',
          body: JSON.stringify({ fullName, email, gender, phone, localityId, password }),
        });
      }
      navigate('/account-pending', { replace: true });
    } catch (err) {
      setError((err as ApiError).message);
      setSubmitting(false);
    }
  }

  if (registrationOpen === false) {
    return (
      <AuthCard
        title="Registration is coming soon"
        subtitle="For any enquiries, contact office@fttmy.org."
      >
        <Link
          to="/login"
          className="inline-flex rounded-lg bg-ink-800 px-6 py-3 text-base font-medium text-white transition-colors hover:bg-ink-900"
        >
          Back to sign in
        </Link>
      </AuthCard>
    );
  }

  if (expired) {
    return (
      <AuthCard
        title="That took too long"
        subtitle="Your registration timed out. Please start again."
      >
        <Link
          to="/login"
          className="inline-flex rounded-lg bg-ink-800 px-6 py-3 text-base font-medium text-white transition-colors hover:bg-ink-900"
        >
          Back to sign in
        </Link>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      wide
      title="Register an account"
      subtitle={
        fromSso ? (
          // Amber, matching the address hint on the sign-in page — an instruction
          // to act on, not an error, so not red.
          <p className="rounded-lg border border-gold-200 bg-gold-100 px-3 py-2 text-sm font-medium text-gold-700">
            Please fill in the info below.
          </p>
        ) : undefined
      }
      footer={
        <Link
          to="/login"
          className="block text-center text-sm text-ink-400 transition-colors hover:text-ink-900"
        >
          ← Back to sign in
        </Link>
      }
    >
      {/* Two columns from tablet width; phones get the plain single column. */}
      <form onSubmit={handleSubmit} className="grid gap-5 sm:grid-cols-2" noValidate>
        {error && (
          <div
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 sm:col-span-2"
          >
            {error}
          </div>
        )}

        <div>
          <Label htmlFor="fullName" required>
            Full name
          </Label>
          <input
            id="fullName"
            type="text"
            autoComplete="name"
            required
            disabled={fromSso || loadingPrefill}
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            className={`mt-2 ${authFieldClass}`}
          />
        </div>

        <div>
          <Label htmlFor="email" required>
            Email
          </Label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            required
            disabled={fromSso || loadingPrefill}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={`mt-2 ${authFieldClass}`}
          />
          {/* Amber text, not a filled note: this explains a locked field rather
              than asking for anything, and a block of colour beside a disabled
              input reads as a problem and unbalances the two columns. */}
          {fromSso && provider && (
            <p className="mt-2 text-xs font-medium text-gold-700">
              Verified by {provider} — this cannot be changed.
            </p>
          )}
          {owner && (
            <SsoNote>
              {owner.label} addresses sign in with {owner.label}, so there is no password to
              set here. <SsoLink provider={owner} /> instead — you will be asked for the
              same details.
            </SsoNote>
          )}
        </div>

        <fieldset>
          <Legend required>Gender</Legend>
          <div className="mt-2 grid grid-cols-2 gap-3">
            {GENDERS.map((option) => (
              <label
                key={option.value}
                className={`cursor-pointer rounded-lg border px-4 py-3 text-center text-base transition-colors ${
                  gender === option.value
                    ? 'border-gold-400 bg-gold-100 font-medium text-gold-700'
                    : 'border-ink-200 bg-white text-ink-900 hover:bg-ink-50'
                }`}
              >
                <input
                  id={`gender-${option.value}`}
                  type="radio"
                  name="gender"
                  value={option.value}
                  checked={gender === option.value}
                  onChange={() => setGender(option.value)}
                  className="sr-only"
                />
                {option.label}
              </label>
            ))}
          </div>
        </fieldset>

        <div>
          <Label htmlFor="phone" required>
            Mobile
          </Label>
          <input
            id="phone"
            type="tel"
            autoComplete="tel"
            required
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className={`mt-2 ${authFieldClass}`}
          />
        </div>

        <div className="sm:col-span-2">
          <Label htmlFor="locality" required>
            Locality
          </Label>
          <div className="mt-2">
            <Combobox
              clearLabel="Clear locality"
              id="locality"
              options={localities}
              value={locality}
              onChange={setLocality}
              className={`${authFieldClass} pr-11`}
            />
          </div>
        </div>

        {!fromSso && (
          <>
            <div>
              <Label htmlFor="password" required>
                Password
              </Label>
              <PasswordInput
                id="password"
                autoComplete="new-password"
                required
                minLength={MIN_PASSWORD}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2"
              />
              <p className="mt-2 text-sm text-gold-600">
                At least {MIN_PASSWORD} characters. You will use it to sign in once your account is approved.
              </p>
            </div>

            <div>
              <Label htmlFor="confirm" required>
                Confirm password
              </Label>
              <PasswordInput
                id="confirm"
                autoComplete="new-password"
                required
                minLength={MIN_PASSWORD}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="mt-2"
              />
            </div>
          </>
        )}

        <button
          type="submit"
          disabled={submitting || loadingPrefill}
          className="w-full rounded-lg bg-ink-800 px-6 py-3.5 text-base font-medium text-white transition-colors hover:bg-ink-900 disabled:cursor-not-allowed disabled:opacity-60 sm:col-span-2"
        >
          {submitting ? 'Sending…' : 'Send'}
        </button>
      </form>
    </AuthCard>
  );
}
